// CSRF helpers per docs/10-security.md §1.7.
// vc_csrf is a non-HttpOnly cookie; we read it from document.cookie and submit
// as X-CSRF-Token on every mutating request.

const CSRF_COOKIE = "vc_csrf";

export function readCsrfTokenFromCookie(cookieHeader?: string): string | null {
  const raw = cookieHeader ?? (typeof document !== "undefined" ? document.cookie : "");
  if (!raw) return null;
  for (const part of raw.split(/;\s*/)) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    if (part.slice(0, eq) === CSRF_COOKIE) {
      return decodeURIComponent(part.slice(eq + 1));
    }
  }
  return null;
}

export const CSRF_HEADER = "X-CSRF-Token";

/** Lazily reissue the vc_csrf cookie by hitting GET /api/auth/whoami.
 *
 * Symptoms that need this: a POST returns 403 even though the session
 * is healthy (GET requests succeed). That happens when an API restart
 * loaded a different CSRF_SECRET than the one that originally signed
 * the operator's cookie, or when the cookie was scrubbed by some
 * browser policy. The /whoami endpoint always re-mints + sets a
 * fresh ``vc_csrf`` cookie via Set-Cookie.
 *
 * Returns whether the refresh succeeded (200 OK from whoami means the
 * session is healthy and the new cookie is good). When false, the
 * session itself is dead — callers should surface "please log in"
 * rather than blindly retrying the original POST (which would just
 * 403 again with the same dead cookie).
 *
 * Lifted from ``lib/api/review.ts`` (2026-06-10), upgraded to return
 * the OK signal (2026-06-11) after operator hit a session-expired
 * 401-cascade that the prior void-return version couldn't distinguish
 * from a normal CSRF-stale recovery. */
export async function refreshCsrfCookie(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/whoami", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    return res.ok;
  } catch {
    /* Best-effort — if whoami fails too, treat the same as "session
       not recoverable" so callers can surface a clean re-auth prompt. */
    return false;
  }
}
